import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { SidebarProvider, useSidebar } from "./contexts/SidebarContext";
import { AppToaster } from "./components/common/AppToaster";
import "./i18n/config";
import { Login } from "./pages/Login";
import { Register } from "./pages/Register";
import { ForgotPassword } from "./pages/ForgotPassword";
import { Dashboard } from "./pages/Dashboard";
import { WorkflowDesigner } from "./pages/WorkflowDesigner";
import { UserManagement } from "./pages/UserManagement";
import { TeamManagement } from "./pages/TeamManagement";
import { ProjectManagement } from "./pages/ProjectManagement";
import { WorkflowInstanceDetail } from "./pages/WorkflowInstanceDetail";
import { ProjectInstances } from "./pages/ProjectInstances";
import { ArchivedProjects } from "./pages/ArchivedProjects";
import { ActivitiesManagement } from "./pages/ActivitiesManagement";
import { FormsManagement } from "./pages/FormsManagement";
import { AddProjectMember } from "./pages/AddProjectMember";
import { EmployeeWorkflows } from "./pages/EmployeeWorkflows";
import { ManagerDashboard } from "./pages/ManagerDashboard";
import { CompanyActivities } from "./pages/CompanyActivities";
import ActivityHistory from "./pages/ActivityHistory";
import { WorkflowOverview } from "./pages/WorkflowOverview";
import { GoogleDriveSettings } from "./pages/GoogleDriveSettings";
import { CompanySettings } from "./pages/CompanySettings";
import { Navbar } from "./components/layout/Navbar";
import { ProtectedRoute } from "./components/auth/ProtectedRoute";
import { UserRole } from "./types";

const AppContent: React.FC = () => {
  const { user, isLoading, isPageHidden } = useAuth();
  const { isCollapsed } = useSidebar();
  const homePath = user?.role === UserRole.VISITOR ? "/project-instances" : "/dashboard";

  if (isLoading) {
    return (
      <div className="app-page bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="app-page bg-gray-50">
      {user && <Navbar />}
      <main
        className={`transition-all duration-300 ${
          user ? (isCollapsed ? "md:ml-16" : "md:ml-64") : ""
        }`}
      >
        <Routes>
          <Route
            path="/login"
            element={user ? <Navigate to={homePath} replace /> : <Login />}
          />

          <Route
            path="/register"
            element={
              <ProtectedRoute requireAdmin>
                <Register />
              </ProtectedRoute>
            }
          />

          <Route
            path="/forgot-password"
            element={
              user ? <Navigate to={homePath} replace /> : <ForgotPassword />
            }
          />

          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Navigate to={homePath} replace />
              </ProtectedRoute>
            }
          />

          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                {user?.role === UserRole.VISITOR ? (
                  <Navigate to={homePath} replace />
                ) : (
                  <Dashboard />
                )}
              </ProtectedRoute>
            }
          />

          <Route
            path="/workflows"
            element={<Navigate to="/project-workflows" replace />}
          />

          <Route
            path="/workflows/new"
            element={
              <ProtectedRoute requireWorkflowManager>
                <WorkflowDesigner />
              </ProtectedRoute>
            }
          />

          <Route
            path="/workflows/:id/edit"
            element={
              <ProtectedRoute requireWorkflowManager>
                <WorkflowDesigner />
              </ProtectedRoute>
            }
          />

          <Route
            path="/workflows/:id/view"
            element={
              <ProtectedRoute>
                <WorkflowDesigner readonly />
              </ProtectedRoute>
            }
          />

          <Route
            path="/workflows/:id"
            element={
              <ProtectedRoute>
                <WorkflowOverview />
              </ProtectedRoute>
            }
          />

          <Route
            path="/workflows/available"
            element={
              <ProtectedRoute>
                <EmployeeWorkflows />
              </ProtectedRoute>
            }
          />

          <Route
            path="/users"
            element={
              <ProtectedRoute requireAdmin>
                <UserManagement />
              </ProtectedRoute>
            }
          />

          <Route
            path="/team-management"
            element={
              <ProtectedRoute requireAdmin>
                <TeamManagement />
              </ProtectedRoute>
            }
          />

          <Route
            path="/project-instances"
            element={
              <ProtectedRoute>
                <ProjectInstances />
              </ProtectedRoute>
            }
          />

          <Route
            path="/archived-projects"
            element={
              <ProtectedRoute>
                <ArchivedProjects />
              </ProtectedRoute>
            }
          />

          <Route
            path="/project-workflows"
            element={
              <ProtectedRoute>
                <ProjectManagement />
              </ProtectedRoute>
            }
          />

          <Route
            path="/project-workflows/:id"
            element={
              <ProtectedRoute>
                <WorkflowOverview />
              </ProtectedRoute>
            }
          />

          <Route
            path="/project-instances/:id"
            element={
              <ProtectedRoute>
                <WorkflowInstanceDetail />
              </ProtectedRoute>
            }
          />

          <Route
            path="/project-instances/:id/add-member"
            element={
              <ProtectedRoute>
                <AddProjectMember />
              </ProtectedRoute>
            }
          />

          <Route
            path="/project-workflows/:id/add-member"
            element={
              <ProtectedRoute>
                <AddProjectMember />
              </ProtectedRoute>
            }
          />

          <Route
            path="/activities"
            element={
              <ProtectedRoute>
                {isPageHidden("activities") ? (
                  <Navigate to={homePath} replace />
                ) : (
                  <ActivitiesManagement />
                )}
              </ProtectedRoute>
            }
          />

          <Route
            path="/forms"
            element={
              <ProtectedRoute>
                {isPageHidden("forms") ? (
                  <Navigate to={homePath} replace />
                ) : (
                  <FormsManagement />
                )}
              </ProtectedRoute>
            }
          />

          <Route
            path="/manager-dashboard"
            element={
              <ProtectedRoute requireAdmin>
                <ManagerDashboard />
              </ProtectedRoute>
            }
          />

          <Route
            path="/company-activities"
            element={
              <ProtectedRoute>
                <CompanyActivities />
              </ProtectedRoute>
            }
          />

          <Route
            path="/activity-history"
            element={
              <ProtectedRoute>
                <ActivityHistory />
              </ProtectedRoute>
            }
          />
          <Route
            path="/google-drive-settings"
            element={
              <ProtectedRoute requireAdmin>
                <GoogleDriveSettings />
              </ProtectedRoute>
            }
          />

          <Route
            path="/settings"
            element={
              <ProtectedRoute requireAdmin>
                <CompanySettings />
              </ProtectedRoute>
            }
          />

          <Route path="*" element={<Navigate to={homePath} replace />} />
        </Routes>
      </main>
    </div>
  );
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <SidebarProvider>
        <Router>
          <AppContent />
          <AppToaster />
        </Router>
      </SidebarProvider>
    </AuthProvider>
  );
};

export default App;
